import { apiClient } from './api';

export interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  duration: number;
  price: number;
  discountPrice?: number;
  isActive: boolean;
  requiredSkills: string[];
  images: string[];
  createdAt: string;
}

export interface CreateServiceData {
  name: string;
  description: string;
  category: string;
  subcategory: string;
  duration: number;
  price: number;
  discountPrice?: number;
  isActive: boolean;
  requiredSkills: string[];
}

export interface UpdateServiceData extends Partial<CreateServiceData> {
  id: string;
}

export interface ServicesResponse {
  services: Service[];
  total: number;
  page: number;
  limit: number;
}

export interface ServiceFilters {
  search?: string;
  category?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

class ServiceApiService {
  private baseUrl = '/services';

  async getServices(filters: ServiceFilters = {}): Promise<ServicesResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

    const response = await apiClient.get<any>(`${this.baseUrl}?${params.toString()}`);
    const data = response?.data ?? response;
    const pagination = response?.pagination;
    return {
      services: data?.services ?? [],
      total: pagination?.total ?? data?.total ?? 0,
      page: pagination?.page ?? data?.page ?? 1,
      limit: pagination?.limit ?? data?.limit ?? 20,
    };
  }

  async getServiceById(id: string): Promise<Service> {
    const response = await apiClient.get<any>(`${this.baseUrl}/${id}`);
    const data = response?.data ?? response;
    return data?.service ?? data;
  }

  async createService(data: CreateServiceData): Promise<Service> {
    const response = await apiClient.post<any>(this.baseUrl, data);
    const resData = response?.data ?? response;
    return resData?.service ?? resData;
  }

  async updateService(data: UpdateServiceData): Promise<Service> {
    const { id, ...updateData } = data;
    const response = await apiClient.put<any>(`${this.baseUrl}/${id}`, updateData);
    const resData = response?.data ?? response;
    return resData?.service ?? resData;
  }

  async getPublicServices(): Promise<ServicesResponse> {
    const response = await apiClient.get<any>(`${this.baseUrl}/public`);
    const data = response?.data ?? response;
    return {
      services: data?.services ?? [],
      total: data?.total ?? 0,
      page: 1,
      limit: 100,
    };
  }

  async deleteService(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

export const serviceService = new ServiceApiService();
